/**
 * Servicio para mantener tracking GPS en segundo plano
 */
class BackgroundGPSService {
  constructor() {
    this.wakeLock = null;
    this.db = null;
    this.isBackgroundMode = false;
  }

  /**
   * Inicializar servicio de GPS en background
   */
  async inicializar() {
    try {
      // 1. Registrar Service Worker
      if ('serviceWorker' in navigator) {
        const registration = await navigator.serviceWorker.register('/service-worker.js');
        console.log('Service Worker registrado:', registration);

        // Escuchar mensajes del Service Worker
        navigator.serviceWorker.addEventListener('message', this.handleServiceWorkerMessage);
      }

      // 2. Abrir base de datos IndexedDB
      this.db = await this.openDatabase();

      // 3. Detectar cuando la app va a segundo plano
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          console.log('App en segundo plano');
          this.isBackgroundMode = true;
          this.onAppBackground();
        } else {
          console.log('App en primer plano');
          this.isBackgroundMode = false;
          this.onAppForeground();
        }
      });

      // 4. Solicitar Wake Lock si está disponible
      await this.requestWakeLock();

      console.log('✓ Servicio de GPS en background inicializado');
    } catch (error) {
      console.error('Error al inicializar servicio GPS background:', error);
    }
  }

  /**
   * Solicitar Wake Lock para mantener la app activa
   */
  async requestWakeLock() {
    try {
      if ('wakeLock' in navigator) {
        this.wakeLock = await navigator.wakeLock.request('screen');
        console.log('✓ Wake Lock activado - la pantalla permanecerá encendida');

        this.wakeLock.addEventListener('release', () => {
          console.log('Wake Lock liberado');
        });
      } else {
        console.warn('Wake Lock API no disponible en este navegador');
      }
    } catch (error) {
      console.error('Error al solicitar Wake Lock:', error);
    }
  }

  /**
   * Liberar Wake Lock
   */
  async releaseWakeLock() {
    if (this.wakeLock) {
      try {
        await this.wakeLock.release();
        this.wakeLock = null;
        console.log('Wake Lock liberado');
      } catch (error) {
        console.error('Error al liberar Wake Lock:', error);
      }
    }
  }

  /**
   * Abrir base de datos IndexedDB
   */
  openDatabase() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open('MogotaxGPS', 1);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result);

      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains('gps-queue')) {
          const store = db.createObjectStore('gps-queue', { keyPath: 'id', autoIncrement: true });
          store.createIndex('traslado_id', 'traslado_id', { unique: false });
          store.createIndex('timestamp', 'timestamp', { unique: false });
        }
      };
    });
  }

  /**
   * Guardar punto GPS en cola local
   */
  async guardarPuntoLocal(trasladoId, coordenadas) {
    if (!this.db) {
      console.error('Base de datos no inicializada');
      return;
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(['gps-queue'], 'readwrite');
      const store = transaction.objectStore('gps-queue');

      const punto = {
        traslado_id: trasladoId,
        latitud: coordenadas.latitud,
        longitud: coordenadas.longitud,
        velocidad: coordenadas.velocidad,
        precision: coordenadas.precision,
        altitud: coordenadas.altitud,
        timestamp: new Date().toISOString(),
        queued_at: Date.now()
      };

      const request = store.add(punto);

      request.onsuccess = () => {
        console.log('Punto GPS guardado localmente:', punto);
        resolve(request.result);
      };

      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Cuando la app va a segundo plano
   */
  async onAppBackground() {
    console.log('🔄 Modo segundo plano activado');

    // Programar sincronización periódica
    if ('serviceWorker' in navigator && 'sync' in ServiceWorkerRegistration.prototype) {
      try {
        const registration = await navigator.serviceWorker.ready;
        await registration.sync.register('sync-gps-points');
        console.log('✓ Sincronización GPS programada');
      } catch (error) {
        console.error('Error al programar sincronización:', error);
      }
    }
  }

  /**
   * Cuando la app vuelve a primer plano
   */
  async onAppForeground() {
    console.log('🔄 Modo primer plano activado');

    // Sincronizar puntos pendientes inmediatamente
    await this.sincronizarPuntosPendientes();

    // Re-solicitar Wake Lock si se perdió
    if (!this.wakeLock) {
      await this.requestWakeLock();
    }
  }

  /**
   * Sincronizar puntos GPS pendientes
   */
  async sincronizarPuntosPendientes() {
    if (!this.db) return;

    try {
      const transaction = this.db.transaction(['gps-queue'], 'readonly');
      const store = transaction.objectStore('gps-queue');
      const request = store.getAll();

      request.onsuccess = async () => {
        const puntosPendientes = request.result;

        if (puntosPendientes.length === 0) {
          console.log('No hay puntos GPS pendientes');
          return;
        }

        console.log(`Sincronizando ${puntosPendientes.length} puntos GPS pendientes...`);

        // Notificar al Service Worker para sincronizar
        if ('serviceWorker' in navigator) {
          const registration = await navigator.serviceWorker.ready;
          await registration.sync.register('sync-gps-points');
        }
      };
    } catch (error) {
      console.error('Error al sincronizar puntos pendientes:', error);
    }
  }

  /**
   * Manejar mensajes del Service Worker
   */
  handleServiceWorkerMessage = (event) => {
    if (event.data && event.data.type === 'GET_AUTH_TOKEN') {
      // Enviar token de autenticación al Service Worker
      const token = localStorage.getItem('token');
      event.ports[0].postMessage({ token });
    }
  };

  /**
   * Guardar token de autenticación en IndexedDB para acceso del Service Worker
   */
  async guardarTokenAuth(token) {
    try {
      const db = await this.openAuthDatabase();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction(['auth'], 'readwrite');
        const store = transaction.objectStore('auth');
        const request = store.put({ key: 'accessToken', value: token });

        request.onsuccess = () => {
          db.close();
          console.log('✓ Token guardado en IndexedDB para Service Worker');
          resolve();
        };
        request.onerror = () => {
          db.close();
          reject(request.error);
        };
      });
    } catch (error) {
      console.error('Error al guardar token en IndexedDB:', error);
    }
  }

  /**
   * Eliminar token de IndexedDB (al cerrar sesión)
   */
  async eliminarTokenAuth() {
    try {
      const db = await this.openAuthDatabase();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction(['auth'], 'readwrite');
        const store = transaction.objectStore('auth');
        const request = store.delete('accessToken');

        request.onsuccess = () => {
          db.close();
          console.log('✓ Token eliminado de IndexedDB');
          resolve();
        };
        request.onerror = () => {
          db.close();
          reject(request.error);
        };
      });
    } catch (error) {
      console.error('Error al eliminar token de IndexedDB:', error);
    }
  }

  /**
   * Abrir base de datos de autenticación
   */
  openAuthDatabase() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open('MogotaxAuth', 1);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result);

      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains('auth')) {
          db.createObjectStore('auth', { keyPath: 'key' });
        }
      };
    });
  }

  /**
   * Verificar si está en modo background
   */
  isInBackground() {
    return this.isBackgroundMode;
  }

  /**
   * Limpiar recursos
   */
  async cleanup() {
    await this.releaseWakeLock();

    if (this.db) {
      this.db.close();
      this.db = null;
    }

    document.removeEventListener('visibilitychange', this.onVisibilityChange);
  }
}

// Exportar instancia singleton
export default new BackgroundGPSService();
